import axios from "axios";

import { apiConfig } from "../config/authConfig";

import { IFuzzySearchRequest } from "../types/system";
import { IMaterial } from "../types/material";

import { CredentialManager } from "../classes/credentialManager";

export namespace MaterialService {
	const credentialManager = CredentialManager.getInstance();

	/**********
	 * Convert one api response object to one Material.
	 * @param data Data for a single material.
	 **********/
	export function mapMaterial(data: any) {
		let item: IMaterial = {
			id: data.id,
			material: data.material,
			category: data.category,
			isHazardous: data.isHazardous,
			isActive: data.isActive,
			createdBy: data.createdBy,
			createdDate: data.createdDate,
			modifiedBy: data.modifiedBy,
			modifiedDate: data.modifiedDate
		}

		return item;
	}

	/**********
	 * Convert an array of api response objects to an array of Materials.
	 * @param data Data for multiple materials.
	 **********/
	export function mapMaterials(data: any) {
		let materials: IMaterial[] = [];

		if (Array.isArray(data) && data.length > 0) {
			data.map((item: any) => {
				materials.push(mapMaterial(item));
			})
		}

		return materials;
	}

	/**********
	 * Gets all materials.
	 **********/
	export async function getMaterials() {
		const response = axios.get(
			apiConfig.default + "/material/all",
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return response;
	}

	/**********
	 * Gets one material.
	 * @param id The material's id
	 **********/
	export async function getMaterial(id: string) {
		const response = axios.get(
			apiConfig.default + "/material/" + id,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return response;
	}

	/**********
	 * Add a new material.
	 * @param details The material details.
	 **********/
	export async function addMaterial(details: IMaterial) {
		const apiResponse = axios.post(
			apiConfig.default + "/material",
			details,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Update an existing material.
	 * @param details The material details.
	 **********/
	export async function updateMaterial(details: IMaterial) {
		const apiResponse = axios.put(
			apiConfig.default + "/material",
			details,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Activate an inactive material.
	 * @param id The ID of the material to activate.
	 **********/
	export async function activateMaterial(id: number) {
		const apiResponse = axios.put(
			apiConfig.default + "/material/" + id + "/activate",
			{},
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Deactivate an active material.
	 * @param id The ID of the material to deactivate.
	 **********/
	export async function deactivateMaterial(id: number) {
		const apiResponse = axios.put(
			apiConfig.default + "/material/" + id + "/deactivate",
			{},
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Search for duplicate existing material names using a fuzzy compare method.
	 **********/
	export async function fuzzySearchForDuplicateMaterials(request: IFuzzySearchRequest) {
		const apiResponse = axios.post(
			apiConfig.default + "/material/fuzzy/material",
			request,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}
}